import type { Table } from "@tanstack/react-table";
import type {
  DocumentRowFragment,
  DocumentTableToolbarFragment,
} from "../client/generated";
import { DocumentTableSearch } from "./DocumentTableSearch";
import { DocumentTableFilter } from "./DocumentTableFilter";
import { DocumentUploadButton } from "./DocumentUploadButton";
import { useTranslation } from "react-i18next";
import { DocumentTableViewOptions } from "./DocumentTableViewOptions";

type Props = {
  fragment: DocumentTableToolbarFragment;
  table: Table<DocumentRowFragment>;
};

export function DocumentTableToolbar({ fragment, table }: Props) {
  const { t } = useTranslation();

  const sortedTags = Array.from(
    new Set(
      fragment.memberships.flatMap((membership) => membership.document.tags),
    ),
  ).sort(compareStringsCaseInsensitive);

  const sortedUploaders = Array.from(
    new Set(
      fragment.memberships.map(
        (membership) => membership.document.uploadedBy.username,
      ),
    ),
  ).sort(compareStringsCaseInsensitive);

  const sortedExtensions = Array.from(
    new Set(
      fragment.memberships.map((membership) => membership.document.extension),
    ),
  ).sort(compareStringsCaseInsensitive);

  return (
    <div className="flex flex-col md:flex-row py-4 gap-2">
      <DocumentTableSearch
        column={table.getColumn("name")!}
        placeholder={t("DocumentTableToolbar.filters.title")}
      />
      <DocumentTableFilter
        column={table.getColumn("tags")!}
        title={t("DocumentTableToolbar.filters.tags")}
        options={sortedTags.map((tag) => ({
          value: tag,
          label: tag,
        }))}
      />
      <DocumentTableFilter
        column={table.getColumn("uploadedBy")!}
        title={t("DocumentTableToolbar.filters.uploader")}
        options={sortedUploaders.map((uploader) => ({
          value: uploader,
          label: uploader,
        }))}
      />
      <DocumentTableFilter
        column={table.getColumn("extension")!}
        title={t("DocumentTableToolbar.filters.extension")}
        options={sortedExtensions.map((extension) => ({
          value: extension,
          label: extension.toUpperCase(),
        }))}
      />
      <div className="ml-auto flex flex-row gap-2">
        <DocumentTableViewOptions table={table} />
        <DocumentUploadButton />
      </div>
    </div>
  );
}

function compareStringsCaseInsensitive(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}
